import { bioLength, bioRefusal, type ArtistProfileBio } from 'fym-shared/src/music24/artistProfile';

/** The counter of one bio field (design c1): "84 / 160", red over the limit. Pure. */
export function bioCounter(text: string, max: number): { count: number; max: number; over: number } {
  const count = bioLength(text);
  return { count, max, over: Math.max(0, count - max) };
}

/** The two texts of the bio tool. */
export interface BioTexts {
  bioShort: string;
  bioLong: string;
}

/**
 * The bio tool state (design c1): the saved text, the text on screen, and whether the text on
 * screen is an AI draft that waits for Keep or Discard.
 */
export interface BioDraftState {
  saved: BioTexts;
  texts: BioTexts;
  /** True from "Write a draft" until Keep, Discard or Save. */
  isAiDraft: boolean;
}

export type BioDraftAction =
  | { type: 'edit'; field: keyof BioTexts; text: string }
  | { type: 'draft'; texts: BioTexts }
  | { type: 'keep' }
  | { type: 'discard' }
  | { type: 'saved'; texts: BioTexts }
  | { type: 'load'; saved: BioTexts };

/** The texts of a saved bio. Pure. */
export function savedTexts(bio: Pick<ArtistProfileBio, 'bioShort' | 'bioLong'>): BioTexts {
  return { bioShort: bio.bioShort, bioLong: bio.bioLong };
}

/** A new tool state on the saved bio. Pure. */
export function initialBioDraft(saved: BioTexts): BioDraftState {
  return { saved, texts: saved, isAiDraft: false };
}

/**
 * The step of the bio tool. A draft fills both fields as an "AI draft"; Keep keeps it on screen
 * (not saved yet); Discard puts back the saved text; Save makes the text on screen the saved one.
 * A new read of the profile replaces the saved text only while nothing is edited. Pure.
 */
export function bioDraftReducer(state: BioDraftState, action: BioDraftAction): BioDraftState {
  switch (action.type) {
    case 'edit':
      return { ...state, texts: { ...state.texts, [action.field]: action.text } };
    case 'draft':
      return { ...state, texts: action.texts, isAiDraft: true };
    case 'keep':
      return { ...state, isAiDraft: false };
    case 'discard':
      return { ...state, texts: state.saved, isAiDraft: false };
    case 'saved':
      return { saved: action.texts, texts: action.texts, isAiDraft: false };
    case 'load':
      return isBioDirty(state) ? { ...state, saved: action.saved } : initialBioDraft(action.saved);
    default:
      return state;
  }
}

/** True when the text on screen is not the saved text: the leave guard is on. Pure. */
export function isBioDirty(state: BioDraftState): boolean {
  return state.texts.bioShort !== state.saved.bioShort || state.texts.bioLong !== state.saved.bioLong;
}

/** Why Save is refused now (the sentence under the field), or null. Pure. */
export function bioSaveRefusal(texts: BioTexts): ReturnType<typeof bioRefusal> {
  return bioRefusal(texts);
}
